import { IPayrollPunchSet, ITimeTotals, IExpensesPayrollData, IPayTypeTotal, IPayType, ITimecardPunchSet, IPunchSetDocument } from "../models"
import * as payTypeTotalHelper from "./payTypeTotalHelper"
import * as punchSetHelper from "./punchSetHelper"
import * as utils from "./utils"
import * as expenseHelper from "./expenseHelper"
import * as timeHelper from "./timeHelper"
import AccountStore from "../stores/AccountStore"

export function getTimeTotals(punchSets: ITimecardPunchSet[] | IPayrollPunchSet[], expenses: IExpensesPayrollData[]): ITimeTotals {
    const account = AccountStore.getCurrentAccount()
    return setupConstructorData(punchSets, expenses, account.PayTypes, account.AccountSettings.FeatureSettings.EnableTimecardExpenses, account.AccountSettings.FeatureSettings.EnableTimecardAmountExpenses)
}

export function getTimeTotalsFromList(timeTotalsList: ITimeTotals[]) {
    const timeTotals = getInitTimeTotals()

    if (!timeTotalsList) {
        timeTotalsList = []
    }

    timeTotals.TotalProductiveHours = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalProductiveHours"))
    timeTotals.TotalProductiveMinutes = timeHelper.convertToMinute(timeTotals.TotalProductiveHours)
    timeTotals.TotalHours = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalHours"))
    timeTotals.TotalMinutes = timeHelper.convertToMinute(timeTotals.TotalHours)
    timeTotals.TotalAmount = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalAmount"))
    timeTotals.TotalPay = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalPay"))
    timeTotals.TotalBill = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalBill"))

    timeTotals.TotalRegularPay = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalRegularPay"))
    timeTotals.TotalRegularBill = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalRegularBill"))

    timeTotals.TotalOTPay = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalOTPay"))
    timeTotals.TotalOTBill = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalOTBill"))

    timeTotals.TotalOT2Pay = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalOT2Pay"))
    timeTotals.TotalOT2Bill = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "TotalOT2Bill"))


    timeTotals.RegularHours = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "RegularHours"))
    timeTotals.RegularMinutes = timeHelper.convertToMinute(timeTotals.RegularHours)
    timeTotals.OvertimeHours = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "OvertimeHours"))
    timeTotals.OvertimeMinutes = timeHelper.convertToMinute(timeTotals.OvertimeHours)
    timeTotals.DoubleOvertimeHours = utils.roundToTwo(utils.sumOfNumberValues(timeTotalsList, "DoubleOvertimeHours"))
    timeTotals.DoubleOvertimeMinutes = timeHelper.convertToMinute(timeTotals.DoubleOvertimeHours)

    timeTotals.PayTypeTotals = []
    timeTotals.ProductiveTimePayTypeTotals = []

    timeTotalsList.forEach(item => {
        item.PayTypeTotals.forEach(itemPayTypeTotal => {
            timeTotals.PayTypeTotals = payTypeTotalHelper.getPayTypeTotals(itemPayTypeTotal, timeTotals.PayTypeTotals)
        })

        item.ProductiveTimePayTypeTotals.forEach(itemProductiveTimePayTypeTotal => {
            timeTotals.ProductiveTimePayTypeTotals = payTypeTotalHelper.getPayTypeTotals(itemProductiveTimePayTypeTotal, timeTotals.ProductiveTimePayTypeTotals)
        })
    })

    timeTotals.PayTypeTotals = OrderByPayTypeTotals(timeTotals.PayTypeTotals)
    timeTotals.ProductiveTimePayTypeTotals = OrderByPayTypeTotals(timeTotals.ProductiveTimePayTypeTotals)

    return timeTotals
}

function setupConstructorData(punchSets: (IPunchSetDocument | ITimecardPunchSet | IPayrollPunchSet)[], expenses: IExpensesPayrollData[], accountPayTypes: IPayType[], includeTimeExpense: boolean, includeAmountExpense: boolean): ITimeTotals {
    const timeTotals = getInitTimeTotals()
    if (punchSets && punchSets.length > 0) {
        timeTotals.TotalProductiveHours = utils.roundToTwo(utils.sumOfNumbers(punchSets.map(ps => punchSetHelper.getTotalProductiveInHours(ps))))
        timeTotals.TotalHours = utils.roundToTwo(utils.sumOfNumbers(punchSets.map(ps => punchSetHelper.getEffectiveTotalDurationInHours(ps))))
        timeTotals.RegularHours = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "RegHours"))
        timeTotals.OvertimeHours = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "OTHours"))
        timeTotals.DoubleOvertimeHours = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "OT2Hours"))

        timeTotals.TotalPay = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "PayTotal"))
        timeTotals.TotalBill = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "BillTotal"))

        timeTotals.TotalRegularPay = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "PayTotalRegularTime"))
        timeTotals.TotalRegularBill = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "BillTotalRegularTime"))

        timeTotals.TotalOTPay = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "PayTotalOvertime"))
        timeTotals.TotalOTBill = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "BillTotalOvertime"))

        timeTotals.TotalOT2Pay = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "PayTotalDoubleOvertime"))
        timeTotals.TotalOT2Bill = utils.roundToTwo(utils.sumOfNumberValues(punchSets, "BillTotalDoubleOvertime"))
    }

    if (includeTimeExpense && expenses) {
        // for now, overtimeFactor and overtime2Factor are hard coded in here.
        // later we may allow payclass selection for expense. This will be a step 2 and it is a better way.
        const overtimeFactor = 1.5, overtime2Factor = 2

        var timeExpenses = expenses.filter(exp => exp.IsTime)
        if (timeExpenses.length > 0) {
            timeTotals.TotalProductiveHours = utils.roundToTwo(timeTotals.TotalProductiveHours + utils.sumOfNumbers(timeExpenses.map(i => expenseHelper.getProductiveTimeInHours(i))))
            timeTotals.TotalHours = utils.roundToTwo(timeTotals.TotalHours + utils.sumOfNumbers(timeExpenses.map(i => expenseHelper.getTimeDurationInHours(i))))

            timeTotals.TotalPay = utils.roundToTwo(timeTotals.TotalPay + utils.sumOfNumbers(timeExpenses.map(exp => (exp.Classification ? exp.Classification.PayRate : 0) * expenseHelper.getExpenseEffectTime(exp))))
            timeTotals.TotalBill = utils.roundToTwo(timeTotals.TotalBill + utils.sumOfNumbers(timeExpenses.map(exp => (exp.Classification ? exp.Classification.BillRate : 0) * expenseHelper.getExpenseEffectTime(exp))))

            var otTimeExpenses = timeExpenses.filter(i => {
                const expensePayType = AccountStore.getCurrentAccount().PayTypes.find(e => e.Id == i.PayTypeId)
                return expensePayType && expensePayType.UseTowardsOvertime == true
            })
            timeTotals.RegularHours = utils.roundToTwo(timeTotals.RegularHours + utils.sumOfNumbers(otTimeExpenses.map(exp => exp.TimeDurations ? exp.TimeDurations.RegularDurationInHours : 0)))
            timeTotals.OvertimeHours = utils.roundToTwo(timeTotals.OvertimeHours + utils.sumOfNumbers(otTimeExpenses.map(exp => exp.TimeDurations ? exp.TimeDurations.OTDurationInHours : 0)))
            timeTotals.DoubleOvertimeHours = utils.roundToTwo(timeTotals.DoubleOvertimeHours + utils.sumOfNumbers(otTimeExpenses.map(exp => exp.TimeDurations ? exp.TimeDurations.OT2DurationInHours : 0)))

            timeTotals.TotalRegularPay = utils.roundToTwo(timeTotals.TotalRegularPay + utils.sumOfNumbers(otTimeExpenses.map(exp => (exp.Classification ? exp.Classification.PayRate : 0) * (exp.TimeDurations ? exp.TimeDurations.RegularDurationInHours : 0))))
            timeTotals.TotalRegularBill = utils.roundToTwo(timeTotals.TotalRegularBill + utils.sumOfNumbers(otTimeExpenses.map(exp => (exp.Classification ? exp.Classification.BillRate : 0) * (exp.TimeDurations ? exp.TimeDurations.RegularDurationInHours : 0))))

            timeTotals.TotalOTPay = utils.roundToTwo(timeTotals.TotalOTPay + utils.sumOfNumbers(otTimeExpenses.map(exp => (exp.Classification ? exp.Classification.PayRate : 0) * (exp.TimeDurations ? exp.TimeDurations.OTDurationInHours : 0) * overtimeFactor)))
            timeTotals.TotalOTBill = utils.roundToTwo(timeTotals.TotalOTBill + utils.sumOfNumbers(otTimeExpenses.map(exp => (exp.Classification ? exp.Classification.BillRate : 0) * (exp.TimeDurations ? exp.TimeDurations.OTDurationInHours : 0) * overtimeFactor)))

            timeTotals.TotalOT2Pay = utils.roundToTwo(timeTotals.TotalOT2Pay + utils.sumOfNumbers(otTimeExpenses.map(exp => (exp.Classification ? exp.Classification.PayRate : 0) * (exp.TimeDurations ? exp.TimeDurations.OT2DurationInHours : 0) * overtime2Factor)))
            timeTotals.TotalOT2Bill = utils.roundToTwo(timeTotals.TotalOT2Bill + utils.sumOfNumbers(otTimeExpenses.map(exp => (exp.Classification ? exp.Classification.BillRate : 0) * (exp.TimeDurations ? exp.TimeDurations.OT2DurationInHours : 0) * overtime2Factor)))
        }

        if (includeAmountExpense) {
            var amountExpenses = expenses.filter(exp => !exp.IsTime)
            if (amountExpenses.length > 0) {
                timeTotals.TotalAmount = utils.roundToTwo(utils.sumOfNumberValues(amountExpenses, "Amount"))
                timeTotals.TotalPay = utils.roundToTwo(timeTotals.TotalPay + timeTotals.TotalAmount)
            }
        }
    }

    timeTotals.TotalProductiveMinutes = timeHelper.convertToMinute(timeTotals.TotalProductiveHours)
    timeTotals.TotalMinutes = timeHelper.convertToMinute(timeTotals.TotalHours)
    timeTotals.RegularMinutes = timeHelper.convertToMinute(timeTotals.RegularHours)
    timeTotals.OvertimeMinutes = timeHelper.convertToMinute(timeTotals.OvertimeHours)
    timeTotals.DoubleOvertimeMinutes = timeHelper.convertToMinute(timeTotals.DoubleOvertimeHours)

    timeTotals.PayTypeTotals = getPayTypeTotals(punchSets, expenses, accountPayTypes)
    timeTotals.ProductiveTimePayTypeTotals = getProductiveTimePayTypeTotals(punchSets, expenses, accountPayTypes)

    return timeTotals
}

export function getInitTimeTotals(): ITimeTotals {
    return {
        TotalProductiveMinutes: 0,
        TotalProductiveHours: 0,
        TotalMinutes: 0,
        TotalHours: 0,
        TotalAmount: 0,
        TotalPay: 0,
        TotalBill: 0,
        TotalRegularPay: 0,
        TotalRegularBill: 0,
        TotalOTPay: 0,
        TotalOTBill: 0,
        TotalOT2Pay: 0,
        TotalOT2Bill: 0,
        RegularMinutes: 0,
        OvertimeMinutes: 0,
        DoubleOvertimeMinutes: 0,
        RegularHours: 0,
        OvertimeHours: 0,
        DoubleOvertimeHours: 0,
        PayTypeTotals: [],
        ProductiveTimePayTypeTotals: [],
        BudgetItems: null,
        TotalBudget: null,
        PayTypeBudgetItems: null
    }
}

function getPayTypeTotals(punchSets: (IPunchSetDocument | IPayrollPunchSet | ITimecardPunchSet)[], expenses: IExpensesPayrollData[], accountPayTypes: IPayType[]): IPayTypeTotal[] {
    let payTypeTotals: IPayTypeTotal[] = []
    if (accountPayTypes == null) {
        return payTypeTotals
    }

    punchSets &&
        punchSets.forEach(ps => {
            var payType = accountPayTypes.find(pt => pt.Id == ps.PayTypeId)
            if (payType != null && !payType.UseTowardsOvertime) {
                var psPayTypeTotal = payTypeTotalHelper.getPayTypeTotal(payType, ps)
                payTypeTotals = payTypeTotalHelper.getPayTypeTotals(psPayTypeTotal, payTypeTotals)
            }
        })

    expenses &&
        expenses.forEach(expense => {
            var payType = accountPayTypes.find(pt => pt.Id == expense.PayTypeId)
            if (payType != null && !payType.UseTowardsOvertime) {
                var expensePayTypeTotal = payTypeTotalHelper.getPayTypeTotalByExpense(payType, expense)
                payTypeTotals = payTypeTotalHelper.getPayTypeTotals(expensePayTypeTotal, payTypeTotals)
            }
        })

    return OrderByPayTypeTotals(payTypeTotals)
}

function getProductiveTimePayTypeTotals(punchSets: (IPunchSetDocument | IPayrollPunchSet | ITimecardPunchSet)[], expenses: IExpensesPayrollData[], accountPayTypes: IPayType[]): IPayTypeTotal[] {
    let payTypeTotals: IPayTypeTotal[] = []

    if (accountPayTypes == null) {
        return payTypeTotals
    }

    punchSets &&
        punchSets.forEach(ps => {
            const payType = accountPayTypes.find(pt => pt.Id == ps.PayTypeId)
            if (payType && payType.UseTowardsOvertime == true) {
                var psPayTypeTotal = payTypeTotalHelper.getPayTypeTotal(payType, ps)
                payTypeTotals = payTypeTotalHelper.getPayTypeTotals(psPayTypeTotal, payTypeTotals)
            }
        })

    expenses &&
        expenses.forEach(expense => {
            const payType = accountPayTypes.find(pt => pt.Id == expense.PayTypeId)
            if (payType && payType.UseTowardsOvertime == true) {
                var expensePayTypeTotal = payTypeTotalHelper.getPayTypeTotalByExpense(payType, expense)
                payTypeTotals = payTypeTotalHelper.getPayTypeTotals(expensePayTypeTotal, payTypeTotals)
            }
        })

    return OrderByPayTypeTotals(payTypeTotals)
}

function OrderByPayTypeTotals(payTypeTotals: IPayTypeTotal[]): IPayTypeTotal[] {
    payTypeTotals = payTypeTotals.sort((a, b) => {
        if (a.PayTypeCode < b.PayTypeCode) {
            return -1
        }
        if (a.PayTypeCode > b.PayTypeCode) {
            return 1
        }
        return 0
    })
    return payTypeTotals
}